import React from 'react';
import '../css/Work.css';
import { pad, useActiveSection, SectionIndex } from './Comp_Editorial.js';

const SECTIONS = [
  { id: 'summary', label: 'Summary' },
  { id: 'skills', label: 'Skills' },
  { id: 'professional-experience', label: 'Professional Experience' },
  { id: 'personal-projects', label: 'Personal Projects' },
  { id: 'additional-experience', label: 'Additional Experience' },
].map((s, i) => ({ ...s, num: pad(i + 1) }));

function Role({ title, dates, org, children }) {
  return (
    <article className="pb-role">
      <div className="pb-role-head">
        <h4>{title}</h4>
        {(org || dates) && (
          <p className="pb-role-meta">
            {org && <span className="pb-role-org">{org}</span>}
            {dates && <span className="pb-role-dates">{dates}</span>}
          </p>
        )}
      </div>
      {children}
    </article>
  );
}

function SectionCaption({ index, headline }) {
  const s = SECTIONS[index];
  return (
    <div className="pb-case-caption">
      <p className="pb-case-label">{s.num} / {s.label}</p>
      {headline && <h3 className="pb-case-headline">{headline}</h3>}
    </div>
  );
}

function Resume() {
  const active = useActiveSection(SECTIONS.map((s) => s.id), 'resume');

  return (
    <main className="pb-case pb-resume fade-in">
      <div className="pb-case-layout">
        <aside className="pb-case-sidebar">
          <SectionIndex eyebrow="Resume" items={SECTIONS} active={active} />
        </aside>

        <div className="pb-case-archive">
          <section id="summary" className="pb-case-identity">
            <p className="pb-case-label">{SECTIONS[0].num} / {SECTIONS[0].label}</p>
            <h1>Senior Technical Product Manager — Platforms & Cross-Functional Delivery</h1>
            <p className="pb-case-reading lead">
              Technical product leader with over a decade of experience building consumer-facing platforms used by millions, including live streaming and creator tools. Owned the broadcaster experience for a live streaming platform licensed to hundreds of partner studios, gathering requirements directly from creators and driving gains in retention, engagement, and revenue. Builds products from requirements to adoption, partnering with engineering, data science, and business stakeholders to turn complex problems into scalable solutions. Currently owns NBCUniversal's shared VOD orchestration platform powering Peacock, and uses AI daily to automate reporting and improve operational efficiency.
            </p>
          </section>

          <section id="skills" className="pb-case-panel">
            <div className="pb-case-row">
              <SectionCaption index={1} headline="Skill Set" />
              <dl className="pb-skills">
                <div>
                  <dt>Live Streaming & Creator Platforms</dt>
                  <dd>Live streaming products, creator and broadcaster tools, creator onboarding, in-stream monetization, engagement and retention, real-time experiences</dd>
                </div>
                <div>
                  <dt>Product & Platform</dt>
                  <dd>Developer and shared platforms, customer discovery, growth metrics, roadmapping, PRDs and technical specs, MVP rollouts and success criteria, A/B testing, product health metrics, Agile</dd>
                </div>
                <div>
                  <dt>Leadership</dt>
                  <dd>Cross-org alignment, platform adoption, executive and stakeholder communication, product demos, training and onboarding, translating technical complexity for non-technical audiences, organizational change management, team leadership (onshore and offshore)</dd>
                </div>
                <div>
                  <dt>Data & Infrastructure</dt>
                  <dd>Data architecture, data validation and governance, Elasticsearch, Grafana, enterprise BI, VOD orchestration, media encoding and packaging, live streaming</dd>
                </div>
                <div>
                  <dt>AI / ML</dt>
                  <dd>Agentic workflows, MCP tool integration, AI skills and context files, LLM prompt architecture, AI-assisted development (Claude Code, Cursor, Augment)</dd>
                </div>
                <div>
                  <dt>Development</dt>
                  <dd>JavaScript, React, Node.js, JSON, HTML/CSS, Git, API and system integration, SQL, NoSQL</dd>
                </div>
                <div>
                  <dt>Design</dt>
                  <dd>UX/UI design, Figma, prototyping, UI component libraries</dd>
                </div>
                <div>
                  <dt>Tools</dt>
                  <dd>Jira, Confluence, GitHub, Slack workflow integration, Monday.com, Airtable</dd>
                </div>
              </dl>
            </div>
          </section>

          <section id="professional-experience" className="pb-case-panel">
            <div className="pb-case-row">
              <SectionCaption index={2} />
              <div className="pb-roles-list">
                <Role title="Technical Product Manager - Peacock VOD" dates="May 2022 – Present" org="NBCUniversal — Seattle, WA (Remote)">
                  <ul>
                    <li>Own the product vision, PRDs, technical specs, and roadmap for NBCUniversal's primary media orchestration platform, the unified VOD delivery pipeline for long-form and short-form media, and manage the full asset catalog for all streaming propositions (Peacock, SkyShowtime, OneApp, NOW/WOW) across North and South America, the UK, and Europe. Scope spans 3 verticals and 5 engineering teams.</li>
                    <li>Lead AI initiatives across product and program teams, driving adoption of Claude, Augment, and Cursor and automating recurring project reporting through AI-assisted workflows and MCP tool integrations, including an AI agent that analyzes tickets, notes, and documents to recommend next actions and improve operational efficiency.</li>
                    <li>Build and test AI skills and context files for product and program workflows, evaluating and tuning prompts and other levers to optimize model output, and define requirements and VOD domain context for a new agentic AI pipeline initiative built on spec-driven development (SDD).</li>
                    <li>Designed and built an AI-assisted project management dashboard (JavaScript, Node.js) as an MVP, setting success criteria and iterating on user feedback before scaling it into a real-time view for Product and PMO leadership. It integrates Jira, GitHub, Monday.com, and Confluence, with validation rules that flag incomplete or inconsistent data and traceability from workstreams to epics, stories, and repositories.</li>
                    <li>Drive alignment across platform engineering teams, senior leadership, and third-party vendors for all of NBCU and Sky's streaming propositions, coordinating global delivery of hundreds of thousands of media assets and presenting technical tradeoffs, risks, and outcomes to executives; lead demos, training, and onboarding for internal and external stakeholders on new platforms.</li>
                    <li>Modernized a fragmented, vendor-dependent workflow into an in-house media orchestration platform, replacing several third-party encoding and packaging vendors, reducing storage needs by over 1,000 TB, and saving $100K+ annually; it became the platform of choice for VOD delivery.</li>
                    <li>Part of the solutioning team for a dedicated short-form pipeline for tentpole events (Olympics, FIFA World Cup, NBA, NFL, Premier League) that delivers clips to Peacock in under 5 minutes from source, including a 20% reduction in encoding time for short-form clips.</li>
                    <li>Designed and deployed the tooling that powers metadata enrichment and unification for an international streaming proposition, unifying catalog metadata across sources and aligning it to the downstream systems that consume it; recognized with an MVP award.</li>
                  </ul>
                </Role>
                <Role title="Product Owner (Content Platform) – Live Streaming Services" dates="December 2019 – February 2022" org="Accretive Technology Group — Greater Seattle Area">
                  <ul>
                    <li>Owned the domain roadmap, vision, and cross-team implementation plan for the broadcaster experience, from initial onboarding through long-term creator success.</li>
                    <li>Supported white-label/black-label licensing of the platform to hundreds of partner studios, gathering requirements directly from broadcaster stakeholders to shape the roadmap for their tooling and experience.</li>
                    <li>Designed and shipped a new creator sign-up workflow, streamlining onboarding for new broadcasters joining the platform.</li>
                    <li>Designed and shipped in-stream monetization features that let viewers pay for specific creator actions, converting live engagement directly into creator revenue.</li>
                    <li>Analyzed user data to design and ship robust filtering and sorting tools, helping creators categorize themselves and helping viewers find what they were looking for.</li>
                    <li>Together, these launches grew creator retention and growth by 20% and viewer engagement and retention by 25% over the year, and drove year-over-year revenue growth of over 40%.</li>
                    <li>Defined KPIs and partnered with data scientists on data-driven decisions, leading multiple teams that received MVP status for increasing streamer retention and engagement (such as in-stream actions).</li>
                    <li>Directly impacted year-over-year revenue growth of over 40%, using case studies and competitor analysis to inform platform strategy, streamer retention, and user engagement.</li>
                    <li>Partnered directly with ML data scientists to design and deploy a machine learning / biometric-based live stream monitoring system (patent pending), defining requirements, success metrics, and rollout, and implementing the industry's compliance gold standard.</li>
                    <li>Championed a “platform mindset,” converting duplicated effort across teams into shared microservices, federated modules, and UI component libraries.</li>
                    <li>Oversaw development of a new developer platform focused on long-term leverage for teams building content-creator-facing tools.</li>
                  </ul>
                </Role>
                <Role title="Senior Technical Product Owner and Team Lead" dates="November 2016 – December 2019" org="Cerner Corporation">
                  <ul>
                    <li>Served as Technical Product Owner for Cerner's enterprise Project and Portfolio platform (Clarity/CA/Broadcom PPM), a shared system supporting multiple business units with on-prem and SaaS solutions.</li>
                    <li>Directly managed a 10-member team, from Principal SWEs to Solution Designers and interns, plus an offshore team in Bangalore, India.</li>
                    <li>Oversaw data, system, and UX architecture across several integrated global enterprise applications, maintaining platform consistency for multiple consuming teams.</li>
                    <li>Owned quarterly and annual roadmapping, KPI tracking, and project metrics, establishing the planning and reporting process for a 25-person cross-functional agile team.</li>
                    <li>Improved BI reporting and financial forecasting accuracy by 85%, reduced project write-offs by 45%, and reduced operating costs by 10 FTEs within the first year of PPM implementation.</li>
                    <li>Designed and developed an internal testing tool suite from concept through shipped release.</li>
                  </ul>
                </Role>
              </div>
            </div>
          </section>

          <section id="personal-projects" className="pb-case-panel">
            <div className="pb-case-row">
              <SectionCaption index={3} />
              <div className="pb-roles-list">
                <Role title={<>WovenWorlds — AI-narrated multiplayer story game, closed alpha (<a href="https://wovenworld.io" target="_blank" rel="noopener noreferrer">wovenworld.io</a>) · Demo available on request</>}>
                  <ul>
                    <li>Designed the UI in Figma and built the application with Claude Code, taking it from design to a working product.</li>
                    <li>Architected the AI narration layer on locally hosted LLMs running on self-hosted AMD ROCm hardware, with a custom prompt architecture for multiplayer storytelling.</li>
                    <li>Built real-time multiplayer infrastructure and released the game to a closed alpha.</li>
                  </ul>
                </Role>
              </div>
            </div>
          </section>

          <section id="additional-experience" className="pb-case-panel">
            <div className="pb-case-row">
              <SectionCaption index={4} />
              <div className="pb-roles-list">
                <Role title="Technical Creative Director, BIGSHOT Inbound" dates="Nov 2015 – Nov 2016">
                  <p>Led designers and developers across web, digital, and print for B2B and B2C clients, working directly with clients to improve their overall marketing strategy through inbound marketing; introduced agile standards and iterative delivery.</p>
                </Role>
                <Role title="Lead Fullstack Developer & System Admin, Brockton Creative Group" dates="Dec 2013 – Nov 2015">
                  <p>Led full-stack development and systems for 50+ clients, architecting technology and data strategy and ensuring PCI compliance.</p>
                </Role>
                <Role title="Owner, Founder, Pixelurbia LLC" dates="Apr 2008 – 2024">
                  <p>Freelance development agency; owned and operated tanktracker.app and pixelbear.studio.</p>
                </Role>
              </div>
            </div>
          </section>
        </div>
      </div>
    </main>
  );
}

export default Resume;
